import {
	useGetClientPoliciesQuery,
	useGetClientPropertiesQuery,
	useUpdateClientRowMutation,
} from '@/features/clients/clientsApi';
import { useGetDealRowQuery, useGetDealsByClientIdQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { isClientPreviewRoute } from '@/hooks/usePreviewMode';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

const useClientContext = (dealIdOverride = null) => {
	const params = useParams();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const previewId = searchParams.get('preview');
	const isClientPreview = isClientPreviewRoute(location.pathname) && previewId;

	// Resolve deal from route overrideId, dealId or preview — only needed when clientId isn't in the URL
	const dealIdForClient = isClientPreview ? null : dealIdOverride || params?.dealId || previewId;
	const { currentData: dealRowForClient } = useGetDealRowQuery(dealIdForClient, {
		skip: !!params?.clientId || !dealIdForClient,
	});

	const clientId =
		params?.clientId ?? (isClientPreview ? previewId : getDealClientId(dealRowForClient));

	const {
		currentData: clientProps,
		isFetching,
		isLoading,
		isUninitialized,
	} = useGetClientPropertiesQuery(clientId, { skip: !clientId });

	const clientRow = clientProps?.clientRow;
	const insight = clientProps?.insight;
	const broker = clientProps?.broker;

	const isProspect = clientRow?.isProspect;
	const insightId = clientRow?.insightId;

	// Use the resolved row's id: the URL clientId may be a legacy hubspotId.
	const dealsQuery = useGetDealsByClientIdQuery(clientRow?.clientId, {
		skip: !clientRow?.clientId,
	});
	const clientDeals = dealsQuery?.currentData ?? [];
	const deals = clientDeals.filter((d) => d.pipeline !== process.env.REACT_APP_PIPELINE_CLAIMS);
	const claims = clientDeals.filter((d) => d.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS);

	const { currentData: policies } = useGetClientPoliciesQuery(
		{ insightId, clientId: clientId ?? clientRow?.clientId },
		{ skip: !clientId && !insightId },
	);

	const [updateClientRow] = useUpdateClientRowMutation();

	useEffect(() => {
		const isNoLongerProspect =
			insight?.ClientType === 0 ||
			(policies ?? []).length > 0 ||
			claims.length > 0 ||
			deals.some((d) => d.pipeline !== process.env.REACT_APP_PIPELINE_NEW_BUSINESS) ||
			deals.length > 1;
		if (isProspect && isNoLongerProspect) {
			updateClientRow({
				clientId: clientRow?.clientId,
				docId: clientRow?.docId,
				properties: { isProspect: false },
			}).unwrap();
		}
	}, [deals, claims, policies, insight?.ClientType]);

	return {
		clientRow,
		insight,
		broker,
		clientId: clientRow?.id ?? clientId,
		clientDocId: clientRow?.id ?? null,
		isClientLoading: Boolean(
			clientId && !clientProps && (isFetching || isLoading || isUninitialized),
		),
	};
};

export default useClientContext;
